import React from 'react';
import { AlertTriangle, FileText, Flame, X } from 'lucide-react';

export const TermsOfUse: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Header — matches AppHeader */}
      <header className="bg-navy-900 border-b border-navy-800 shadow-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-sm"
              title="Close this tab"
            >
              <X className="w-4 h-4" />
              <span>Close</span>
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center shadow-sm">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-[17px] font-bold text-white font-brand tracking-tight">Terms of Use</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">

          {/* Data Disclaimer Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold text-amber-800 mb-3">Data Disclaimer</h2>
                <div className="text-amber-700 space-y-3">
                  <p>
                    Fuel price data is obtained from{' '}
                    <a
                      href="https://www.gov.uk/guidance/access-fuel-price-data"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-800 underline hover:text-amber-600"
                    >
                      www.gov.uk/guidance/access-fuel-price-data
                    </a>
                    {' '}and various retailer APIs.
                  </p>
                  <p>
                    <strong>We are not responsible for any errors, missing information, or inaccuracies</strong> in the fuel price data.
                    This service should not be relied upon for critical decisions, and users should always verify
                    information independently before taking action.
                  </p>
                  <p>
                    <strong>We cannot be held accountable for any financial loss or inconvenience</strong> that may result from using this website.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Terms of Use */}
          <div className="prose prose-gray max-w-none">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-6 h-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-gray-900 m-0">Terms of Use</h2>
            </div>

            <p className="text-gray-700 mb-6">
              By accessing or using FuelHunt, you agree to these Terms of Use. If you do not agree, please do not use this website.
            </p>

            <div className="space-y-6">
              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Purpose</h3>
                <p className="text-gray-700">
                  FuelHunt provides fuel price information for general informational purposes only.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">No Guarantee</h3>
                <p className="text-gray-700">
                  We make no warranties about the accuracy, completeness, or reliability of any data or content provided.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">User Responsibility</h3>
                <p className="text-gray-700">
                  You are responsible for verifying information independently before relying on it.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Limitation of Liability</h3>
                <p className="text-gray-700">
                  FuelHunt and its developers are not liable for any loss, damage, or inconvenience resulting from use of this site or reliance on its information.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Changes</h3>
                <p className="text-gray-700">
                  We may update or modify these Terms and the services offered at any time without prior notice. Continued use of the site indicates acceptance of any changes.
                </p>
              </section>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                For questions about these terms, please contact us at{' '}
                <a href="mailto:fuelhunt@hotmail.com" className="text-amber-600 hover:text-amber-700">
                  fuelhunt@hotmail.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfUse;
